/**
 * Upload Service
 * Handles all upload-related API calls
 * @module services/upload/uploadService
 */

import httpClient from '@/utils/httpClient';
import { ENDPOINTS, getEndpointPath } from '@/api/endpoints';

const uploadService = {
  /**
   * Get my documents
   * @returns {Promise} - Response with documents data
   */
  getMyDocuments: async () => {
    try {
      const response = await httpClient.get(getEndpointPath(ENDPOINTS.UPLOAD.MY_DOCUMENTS));
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Upload my document for management review
   * @param {FormData} formData - Form data with file (+ optional description)
   * @returns {Promise} - Response with uploaded document data
   */
  uploadMyDocument: async (formData) => {
    try {
      const response = await httpClient.post(getEndpointPath(ENDPOINTS.UPLOAD.MY_DOCUMENTS), formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * List all uploaded documents
   * @returns {Promise} - Response with documents list
   */
  listDocuments: async () => {
    try {
      const response = await httpClient.get(getEndpointPath(ENDPOINTS.UPLOAD.LIST));
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Delete a document by ID
   * @param {string} id - Document ID
   * @returns {Promise} - Response
   */
  deleteDocument: async (id) => {
    try {
      const encodedId = String(id)
        .split('/')
        .map((segment) => encodeURIComponent(segment))
        .join('/');
      const response = await httpClient.delete(getEndpointPath(ENDPOINTS.UPLOAD.DELETE, encodedId));
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Upload a document
   * @param {FormData} formData - Form data with document file
   * @returns {Promise} - Response with uploaded document data
   */
  uploadDocument: async (formData) => {
    try {
      const response = await httpClient.post(getEndpointPath(ENDPOINTS.UPLOAD.DOCUMENT), formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Get document statistics
   * @param {string} publicId - Document public ID
   * @returns {Promise} - Response with document statistics
   */
  getDocumentStats: async (publicId) => {
    try {
      const response = await httpClient.get(getEndpointPath(ENDPOINTS.UPLOAD.DOCUMENT_STATS, publicId));
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
};

export default uploadService;

